import { httpsCallable } from 'firebase/functions'
import { functions } from './firebase'
import type { SlipResult } from '@shared/slips'
import type { FeatureKey, Features } from '@shared/features'
import type { ContactSettings } from '@shared/contact'
import type { CompanyStats } from '@shared/companyStats'
import type { PeriodHealth } from '@shared/periodHealth'
import type { LoadTestReport, LoadTestScenario } from '@shared/loadTest'

/**
 * Klientsidan av Cloud Functions.
 *
 * Allt som måste vara sant oavsett vad klienten påstår – att en kod bara kan
 * aktiveras en gång, att en rabatt bara kan lösas in en gång per häfte, att
 * bara superadmin ändrar roller – ligger bakom de här anropen.
 */

/**
 * Firebase väntar som standard i 70 sekunder innan ett anrop ger upp. Det är
 * för länge för något man gör stående i en kassakö – knappen skulle stå och
 * snurra i över en minut på dålig täckning. Varje anrop får därför en tid som
 * matchar hur länge det rimligen får ta.
 */
function callable<Request, Response>(name: string, timeoutMs = 20_000) {
  const fn = httpsCallable<Request, Response>(functions, name, { timeout: timeoutMs })
  return async (payload: Request): Promise<Response> => {
    const result = await fn(payload)
    return result.data
  }
}

/* ---------------- Köpare ---------------- */

export const activateCode = callable<
  { code: string; via?: 'qr' | 'typed' },
  { bookletId: string; periodName: string; startsInFuture: boolean; startDate: string }
>('activateCode', 20_000)

export const redeemOffer = callable<
  { offerId: string },
  {
    redemptionId: string
    shortId: string
    redeemedAt: number
    valueSek: number
    totalSavedSek: number
    remaining: number
  }
>('redeemOffer', 15_000)

export const deleteMyAccount = callable<{ confirm: 'RADERA' }, { ok: true }>('deleteMyAccount')

/* ---------------- Lagledare ---------------- */

export const registerDigitalSale = callable<
  { codeId?: string; teamId: string; bookletId: string; buyerEmail: string; playerName?: string },
  { codeId: string; emailSent: boolean }
>('registerDigitalSale')

/* ---------------- Admin ---------------- */

export const generateCodes = callable<
  { bookletId: string; teamId: string; count: number; note?: string; isTest?: boolean },
  { batchId: string; created: number }
>('generateCodes', 120_000)

/**
 * Heter `exportCodes` och inte `exportCodeBatch` av en trist anledning:
 * den ursprungliga funktionen skapades under en deploy som havererade
 * halvvägs och fick aldrig rättigheten som gör den anropbar från webben.
 * Den behörigheten sätts bara när en funktion skapas, inte när den
 * uppdateras – så den enda vägen framåt var ett nytt namn.
 *
 * Den övergivna funktionen är borttagen ur projektet. Namnet står kvar som
 * det är: att byta tillbaka skulle skapa ännu en ny funktion utan att ge
 * något.
 */
export const exportCodes = callable<
  { batchId?: string; bookletId?: string },
  { fileName: string; contentBase64: string }
>('exportCodes', 540_000)

export const revokeRedemption = callable<
  { redemptionId: string; reason: string },
  { ok: true }
>('revokeRedemption')

/**
 * Raderar ett konto som gallringen flaggat som stilla i två år.
 *
 * Kräver superadmin och en motivering – se resonemanget i users.ts.
 */
export const deleteUserAccount = callable<{ uid: string; reason: string }, { ok: true }>(
  'deleteUserAccount',
  30_000,
)

export const setUserRole = callable<
  { uid: string; role: 'buyer' | 'teamLeader' | 'admin' | 'superadmin'; teamId?: string | null },
  { ok: true }
>('setUserRole')

export interface TestDataCounts {
  booklets: number
  companies: number
  offers: number
  teams: number
  codes: number
  codeBatches: number
  redemptions: number
  demoUsers: number
}

export const createTestBooklet = callable<
  Record<string, never>,
  {
    bookletId: string
    companies: number
    offers: number
    teams: number
    codes: number
    activated: number
    paid: number
    /** Registrerade fysiska lappar. */
    onSlips: number
    /** Häften som gått via den digitala beställningen. */
    ordered: number
    /** Outdelade lappar att skriva in när registreringen ska provas. */
    slipCodes: string[]
    redemptions: number
    redeemedSharePercent: number
    demoAccounts: { email: string; purpose: string }[]
    demoPassword: string
  }
>('createTestBooklet', 540_000)

export const clearTestData = callable<
  { confirm?: string; dryRun?: boolean },
  { dryRun: boolean; counts: TestDataCounts }
>('clearTestData', 540_000)

export const runBackupNow = callable<
  Record<string, never>,
  { target: string; operation: string; retention: 'redan-satt' | 'satt' | 'misslyckades' }
>('runBackupNow', 60_000)

/**
 * Räknar om vad inloggningssidan säger att häftet är värt.
 *
 * Sker annars av sig självt: vid varje ändring av ett erbjudande eller häfte,
 * och en gång per dygn. Knappen finns för stunden direkt efter en
 * driftsättning, när dokumentet ännu inte hunnit skapas.
 */
/**
 * Räknar fram raden utan att publicera den.
 *
 * Tar med testhäftet, så att föreningen kan se hur inloggningssidan kommer
 * att se ut innan den första riktiga perioden är upplagd.
 */
export const previewPublicSummary = callable<
  { includeTest?: boolean },
  {
    summary: {
      bookletId: string | null
      periodName: string | null
      offerCount: number
      totalValueSek: number
      priceSek: number
    } | null
    booklets: number
    expired: number
  }
>('previewPublicSummary', 60_000)

export const refreshPublicSummaryNow = callable<
  Record<string, never>,
  {
    summary: {
      bookletId: string | null
      periodName: string | null
      offerCount: number
      totalValueSek: number
      priceSek: number
    } | null
    /** Antal riktiga häften funktionen hade att välja mellan. */
    booklets: number
    expired: number
  }
>('refreshPublicSummaryNow', 60_000)

export const backupStatus = callable<
  Record<string, never>,
  {
    bucket: string
    retentionActive: boolean
    retentionDays: number
    backups: string[]
    total: number
  }
>('backupStatus')

/** Läser in senaste kopian i testdatabasen. Rör aldrig den riktiga. */
export const startRestoreDrill = callable<
  Record<string, never>,
  { source: string; operation: string }
>('startRestoreDrill', 60_000)

/** Jämför testdatabasen med den riktiga, samling för samling. */
export const restoreDrillStatus = callable<
  { operation?: string },
  {
    done: boolean | null
    importError: string | null
    rows: Array<{ collection: string; original: number; restored: number }>
  }
>('restoreDrillStatus', 60_000)

export const hardDelete = callable<
  { collection: 'companies' | 'offers' | 'teams' | 'booklets' | 'codes'; id: string; reason: string },
  { ok: true }
>('hardDelete')

/* ---------- Beställningar ---------- */

export const createOrder = callable<
  {
    bookletId: string
    teamId: string
    buyerName: string
    buyerEmail: string
    playerName: string
    quantity: number
  },
  {
    reference: string
    /** Nyckeln till beställningens sida. Följer med i länken. */
    accessToken: string
    quantity: number
    totalSek: number
    unitPriceSek: number
    swishNumber: string
    message: string
    periodName: string
    teamName: string
  }
>('createOrder', 30_000)

/**
 * Beställningen, för den som har hela länken.
 *
 * Både referens och nyckel krävs. Klienten läser inte längre dokumentet
 * direkt – se resonemanget i firestore.rules.
 */
export const orderStatus = callable<
  { reference: string; token: string },
  {
    id: string
    reference: string
    periodName: string
    teamName: string
    playerName: string
    buyerName: string
    quantity: number
    unitPriceSek: number
    totalSek: number
    status: 'awaitingPayment' | 'paid' | 'cancelled'
    swishNumber: string
    paymentUrl: string | null
    cancelReason: string | null
    codeIds: string[]
    message: string
  }
>('orderStatus', 20_000)

export const markOrderPaid = callable<
  { reference: string; source?: 'manual' | 'swishReport' | 'swishApi' },
  { alreadyPaid: boolean; codeIds: string[] }
>('markOrderPaid', 30_000)

/** Svaret bär med sig mejlet för testbeställningar, så att det går att visa upp. */
export const remindOrder = callable<
  { reference: string },
  { sent: true; preview: { to: string; subject: string; text: string } | null }
>('remindOrder', 30_000)

export const cancelOrder = callable<
  { reference: string; reason?: string },
  { cancelled: true }
>('cancelOrder')

/**
 * Perioder och lag att beställa till.
 *
 * `forTeamId` är lagets id ur länken. Pekar det på ett testlag svarar
 * funktionen med testhäftet och testlagen i stället för de riktiga, så att
 * beställningsflödet går att prova. `isTest` säger vilket läge svaret gäller.
 */
export const orderOptions = callable<
  { forTeamId?: string },
  {
    booklets: { id: string; periodName: string; priceSek: number }[]
    teams: { id: string; name: string; bookletsPerPlayer: number | null; acceptsOrders: boolean }[]
    isTest: boolean
  }
>('orderOptions', 20_000)

export const reconcileOrders = callable<
  { payments: { reference: string; amountSek: number }[] },
  { settled: string[]; skipped: { reference: string; reason: string }[] }
>('reconcileOrders', 300_000)

export const handOutCode = callable<
  { codeId: string; toEmail?: string },
  { emailSent: boolean }
>('handOutCode', 30_000)

export const undoHandOut = callable<{ codeId: string }, { ok: true }>('undoHandOut')

/* ---------- Funktioner ---------- */

/**
 * Föreningens kontaktuppgifter, som de visas i integritetspolicyn.
 *
 * Går via servern och inte direkt mot databasen: står fel adress i policyn
 * når ingen fram med en begäran om radering, så fälten valideras och
 * ändringen hamnar i loggen.
 */
export const setContact = callable<ContactSettings, { contact: ContactSettings }>(
  'setContact',
  30_000,
)

/** Slår på eller av en funktion. Bara superadmin, och loggas. */
export const setFeature = callable<
  { key: FeatureKey; enabled: boolean },
  { features: Features }
>('setFeature', 30_000)

/* ---------- Fysiska lappar ---------- */

/**
 * Registrerar utdelade papperslappar på en hållare.
 *
 * Utan `forEmail` registrerar den inloggade på sig själv. Med `forEmail` gör
 * en lagledare det åt en förälder. Svaret är per kod, inte per anrop: en
 * felläst lapp bland fem ska inte kasta de fyra andra.
 */
export const registerSlips = callable<
  { codes: string[]; forEmail?: string; playerName?: string },
  { results: SlipResult[]; registered: number; createdAccount: boolean }
>('registerSlips', 60_000)

/**
 * Ett företags siffror för en period – räckvidd, erbjudanden och när på
 * veckan kunderna kommer. Räknas på servern, eftersom antalet olika häften
 * inte går att få fram ur de förberäknade dygnssummorna.
 */
export const companyStats = callable<{ bookletId: string; companyId: string }, CompanyStats>(
  'companyStats',
  60_000,
)

/** Räknar om häftenas hälsa för en period nu, i stället för att vänta till natten. */
export const refreshPeriodHealth = callable<{ bookletId: string }, PeriodHealth>(
  'refreshPeriodHealth',
  300_000,
)

/** Hela statistiken för en period som en Excel-fil, ett blad per vy. */
export const exportStatistics = callable<
  { bookletId: string },
  { fileName: string; contentBase64: string }
>('exportStatistics', 540_000)

/**
 * Lunchrusningen i produktion: påhittade köpare löser in i testhäftet med
 * samma transaktion som en riktig köpare. Bara superadmin, och bara om det
 * finns ett testhäfte. Tar upp till några minuter.
 */
export const runLoadTest = callable<
  { users?: number; inFlight?: number; scenario?: LoadTestScenario },
  LoadTestReport
>(
  'runLoadTest',
  540_000,
)
